import { useEffect } from 'react';
import { useStore } from '@nanostores/react';
import { atom } from 'nanostores';

export type PurchaseRequestResult<Confirm, Close = undefined> =
  | { reason: 'confirm'; data: Confirm }
  | { reason: 'close'; data?: Close };

type PurchaseRequestState<Data> = {
  data: Data;
  key: number;
};

export type PurchaseRequest<Data, Confirm, Close = undefined> = {
  request: (data: Data) => Promise<PurchaseRequestResult<Confirm, Close>>;
  confirm: (result: Confirm) => void;
  dismiss: (data?: Close) => void;
  setBusy: (isBusy: boolean) => void;
  useRequestState: () => PurchaseRequestState<Data> | null;
  clear: () => void;
  useIsOpen: () => boolean;
  useHost: () => void;
};

export function createPurchaseRequest<Data, Confirm, Close = undefined>(): PurchaseRequest<Data, Confirm, Close> {
  const $state = atom<PurchaseRequestState<Data> | null>(null);
  const $isOpen = atom(false);
  let settlePending: ((result: PurchaseRequestResult<Confirm, Close>) => void) | null = null;
  let isBusy = false;
  let lastKey = 0;

  const settle = (result: PurchaseRequestResult<Confirm, Close>) => {
    const pending = settlePending;
    if (pending == null) return;
    settlePending = null;
    isBusy = false;
    $isOpen.set(false);
    pending(result);
  };

  return {
    request: (data) => {
      if (isBusy) return Promise.resolve({ reason: 'close' });
      settle({ reason: 'close' });
      lastKey += 1;
      $state.set({ data, key: lastKey });
      $isOpen.set(true);
      return new Promise((resolve) => {
        settlePending = resolve;
      });
    },
    confirm: (result) => settle({ reason: 'confirm', data: result }),
    dismiss: (data) => settle({ reason: 'close', data }),
    setBusy: (busy) => {
      isBusy = busy && settlePending != null;
    },
    useRequestState: () => useStore($state),
    clear: () => {
      if (!$isOpen.get()) $state.set(null);
    },
    useIsOpen: () => useStore($isOpen),
    useHost: () => {
      useEffect(() => () => settle({ reason: 'close' }), []);
    },
  };
}
